import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@/shared/lib/theme';
import {
  METABASE_IFRAME_HEIGHT_CLASS,
  METABASE_IFRAME_SKELETON_CLASS,
} from '@/features/dashboard/model/metabase-embed-height';
import { withMetabaseTheme } from '@/features/dashboard/model/metabase-theme';
import { cn } from '@/shared/lib/cn';
import { EmptyState } from '@/shared/components/empty-state';
import { Skeleton } from '@/shared/ui/skeleton';
import type { LucideIcon } from 'lucide-react';

interface MetabaseIframePanelProps {
  src?: string;
  title: string;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  icon: LucideIcon;
}

export function MetabaseIframePanel({
  src,
  title,
  isLoading,
  isError,
  errorMessage,
  icon: Icon,
}: MetabaseIframePanelProps) {
  const { resolvedTheme } = useTheme();
  const themedSrc = src ? withMetabaseTheme(src, resolvedTheme) : undefined;

  if (isLoading) {
    return <Skeleton className={cn('w-full rounded-2xl', METABASE_IFRAME_SKELETON_CLASS)} />;
  }

  if (isError || !themedSrc) {
    return (
      <EmptyState
        icon={Icon}
        title="Dashboard indisponível"
        description={errorMessage ?? 'Não foi possível carregar o Metabase.'}
        className="py-20"
      />
    );
  }

  return (
    <div className="glass rounded-2xl">
      <iframe
        key={`${themedSrc}-${resolvedTheme}`}
        src={themedSrc}
        title={title}
        className={cn('w-full border-0 bg-background', METABASE_IFRAME_HEIGHT_CLASS)}
        allowTransparency
      />
    </div>
  );
}

export function useUserMetabaseEmbed(kind: 'owner' | 'nps', enabled: boolean) {
  return useQuery({
    queryKey: ['metabase', 'profile', kind],
    queryFn: async (): Promise<{ iframeUrl: string }> => {
      const res = await fetch(`/bff/metabase/profile/${kind}`, { credentials: 'same-origin' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        throw new Error(body.message ?? 'Metabase indisponível');
      }
      return res.json() as Promise<{ iframeUrl: string }>;
    },
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}
